import { useVoucher } from "app/hooks/shared/useVoucher";
import { CartProviderProps } from "app/providers/CartProvider";
import {
  OrderProviderProps,
  useOrderContext,
} from "app/providers/OrderProvider";
import { useMemo } from "react";
import { toast } from "react-toastify";
import {
  CreateCartPayload,
  GetOrderResponse,
} from "shared/api/middleware-api-client";
import { clearVoucher, voucherState } from "shared/store/voucher/voucherStore";
import Icon, { IconTypes } from "shared/ui/icon";
import Text from "shared/ui/typography/Text";
import { concatClassnames } from "shared/utils/concatClassnames";
import { getFormattedPrice } from "shared/utils/getFormattedPrice";
import { useSnapshot } from "valtio";
import { DELIVERY_PRICES } from "shared/config/pricing";

interface PriceTableProps {
  className?: string;
  entity: GetOrderResponse; // entity is used because PriceTable can be used with cart or with order
  context: OrderProviderProps;
}

const PriceTable = ({ className, entity, context }: PriceTableProps) => {
  const { order, hasVouchersAlready, getReadableDiscountRecordPrice } =
    useOrderContext();
  const { addVoucher, isLoadingVoucher } = useVoucher();
  // Calculate the warranty package based on the entity and context
  const warrantyPackage = useMemo(() => {
    if (entity.warrantyPackage && entity.warrantyPackage.code !== "") {
      return entity.warrantyPackage;
    } else if (
      "warrantyPackage" in context &&
      context.warrantyPackage &&
      context.warrantyPackage.code !== ""
    ) {
      return context.warrantyPackage;
    }

    return entity.warrantyPackage;
  }, [entity, context]);

  // Calculate the discount records based on the entity and context
  const discountRecords = useMemo(() => {
    if (order && order.discountRecords) {
      return order.discountRecords.map((record) => ({
        title: record.title,
        price: getReadableDiscountRecordPrice(record),
        id: record.id,
      }));
    }

    return [];
  }, [order]);

  // Calculate self-assembly delivery costs
  const selfAssemblyDeliveryCosts = useMemo(() => {
    if (order && order.isSelfAssembly) {
      return DELIVERY_PRICES.SELF_ASSEMBLY;
    }
    return 0;
  }, [order]);

  // Handler for removing voucher
  const onRemoveVoucherClick = async () => {
    if (hasVouchersAlready) {
      try {
        await addVoucher("");
        toast.success("Voucher is verwijderd!");
      } catch (e: any) {
        toast.error("Kon voucher niet verwijderen");
      }
    } else {
      clearVoucher();
    }
  };

  // Condition to show warranty
  const showWarranty = useMemo(() => {
    return (
      warrantyPackage !== undefined &&
      warrantyPackage.packageId !== "no_package_now" &&
      warrantyPackage.packageId !== "decide_later"
    );
  }, [warrantyPackage]);

  // Text to display for the warranty package
  const warrantyPackageText =
    entity.isSelfAssembly && warrantyPackage?.packageId === "no_package_full"
      ? "Geen GewoonZeker"
      : warrantyPackage?.name.split("-")[0];

  // Voucher data from voucher store
  const { voucher } = useSnapshot(voucherState);
  // Voucher price
  const voucherPrice = getFormattedPrice(
    "getVoucherDiscount" in context ? context.getVoucherDiscount() : 0,
  );

  // Order extra discount
  const orderExtraDiscounts =
    "extraDiscounts" in entity ? entity.extraDiscounts : null;

  const hasAlreadyPaid = order?.paymentData?.paid && order.paymentData.paid > 0;
  return (
    <div className={concatClassnames("gg-grid gg-grid-cols-12", className)}>
      {isLoadingVoucher && (
        <div className="gg-absolute gg-inset-0 gg-z-10 gg-grid gg-h-full gg-w-full gg-place-items-center gg-backdrop-blur-lg">
          <div
            className="gg-motion-reduce:animate-[spin_1.5s_linear_infinite] gg-inline-block gg-h-16 gg-w-16 gg-animate-spin gg-rounded-full gg-border-4 gg-border-solid gg-border-current gg-border-r-transparent gg-align-[-0.125em]"
            role="status"
          ></div>
        </div>
      )}
      <div className="gg-col-span-9 gg-grid gg-gap-y-2.5 sm:gg-col-span-6 lg:gg-col-span-8">
        <Text>Subtotaal</Text>

        {entity.isSelfAssembly && <Text>Bezorgkosten</Text>}

        {orderExtraDiscounts && <Text>Korting extra product</Text>}

        {showWarranty && <Text>{warrantyPackageText}</Text>}
        {discountRecords.length
          ? discountRecords.map((record) => (
              <Text
                key={`discount-record-${record.title}-title`}
                className="gg-flex gg-items-center gg-gap-x-3"
              >
                {!("shoppingCart" in entity) ||
                  (entity.shoppingCart?.sampleSale === false &&
                    record.id !== "orderDiscount" &&
                    !hasAlreadyPaid && (
                      <button
                        onClick={() => onRemoveVoucherClick()}
                        data-testid="remove-voucher-button"
                        className={
                          record.id === "extraDoors" ? "gg-hidden" : ""
                        }
                      >
                        <Icon name={IconTypes.REMOVE} />
                      </button>
                    ))}
                {record.title}
              </Text>
            ))
          : null}

        {voucher && (
          <div className="gg-flex gg-gap-x-3">
            <Text>Voucher: {voucher.name}</Text>

            {!hasAlreadyPaid && (
              <button
                onClick={() => onRemoveVoucherClick()}
                data-testid="remove-voucher-button"
              >
                <Icon name={IconTypes.REMOVE} />
              </button>
            )}
          </div>
        )}
      </div>
      <div className="gg-col-span-3 gg-grid gg-gap-y-2.5 gg-justify-self-end gg-text-right sm:gg-col-span-6 sm:gg-col-start-7 lg:gg-col-span-4 lg:gg-pl-0">
        <Text data-testid="subtotal-price" bold>
          {context.getFormattedSubTotalPrice()}
        </Text>

        {entity.isSelfAssembly && (
          <Text bold>{getFormattedPrice(selfAssemblyDeliveryCosts)}</Text>
        )}

        {orderExtraDiscounts && (
          <Text bold className="gg-text-green-300">
            - {getFormattedPrice(orderExtraDiscounts)}
          </Text>
        )}

        {showWarranty && (
          <Text bold>{getFormattedPrice(warrantyPackage?.price)}</Text>
        )}
        {discountRecords.length
          ? discountRecords.map((record) => (
              <Text key={`discount-record-${record.title}-price`} bold>
                {record.price}
              </Text>
            ))
          : null}

        {voucher ? (
          <Text bold className="gg-text-green-300">
            -{voucherPrice}
          </Text>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};
export default PriceTable;
